/* KROK 3 — ČÍM SE DÁ CÍL DOFINANCOVAT (oprava 5. 10. 2026).
 * ============================================================================
 * Majitel 5. 10. 2026 ke snímku karty Bydlení, kde volba „zástava nemovitosti
 * (rodiče)“ nabízela čip „auto škoda rapid · 200 000 Kč“: „Škoda Rapid nejde
 * zastavit přece 😃“.
 *
 * STARÁ CHYBA: krok 3 bral nefinanční majetek po KATEGORIÍCH analýzy
 * a k zástavě nabízel „dědictví + nemovitosti“. Jenže `majetek.nemovitosti`
 * je jedna tabulka pro byt, auto, cennosti i zlato (řádky n1–n6) a dědictví
 * se nabízelo bez ohledu na to, jestli je to byt po rodičích, nebo peníze.
 * Auto ze stejné tabulky jako byt tak šlo „zastavit“ k hypotéce.
 *
 * PRAVIDLA (česká hypoteční praxe):
 *   · ZÁSTAVA — jen nemovitost (byt, dům, pozemek, rekreační objekt) klienta
 *     nebo třetí osoby (rodičů). Auto, cennosti, podíl ve firmě, peníze ani
 *     dědictví v penězích se k hypotéce nezastavují. Družstevní byt taky ne
 *     (klient vlastní podíl v družstvu) — ten jde jen prodat.
 *   · PRODEJ NEMOVITOSTI — jen nemovitosti. Vlastní bydlení jen když se klient
 *     stěhuje; analýza to neříká, proto se nabídne se štítkem „jen při
 *     stěhování“. Nemovitost po rodičích (dědictví) se štítkem „očekávané“.
 *   · JINÝ MAJETEK — peníze z prodeje movitého majetku (auto, cennosti, zlato,
 *     podíl ve firmě) a dědictví v penězích nebo neurčené („očekávané“). Žádné
 *     nemovitosti. Účty a investice (fondy, životko) sem NEPATŘÍ: plán je už
 *     rozděluje ve zdrojích (krok 1 — pool solveru, fondy od metodiky
 *     2026-10-05); tady by se tatáž koruna započítala podruhé.
 *   · NEZNÁMÝ DRUH (řádek bez typu mimo datový model, typ mimo číselník, nebo
 *     typ „nemovitost“ u položky, jejíž název mluví o autu / zlatu / penězích)
 *     → k zástavě ani k prodeji nemovitosti NE; jen „jiný majetek“ se štítkem
 *     „ověřte druh“. Nový řádek tabulky nefinančního majetku se zakládá jako
 *     „Nemovitost k bydlení“ — kdo typ nepřepne, má auto vedené jako byt.
 *     Proto u takového řádku (mimo řádek n1) musí nemovitost poznat i NÁZEV
 *     (byt, dům, pozemek, 2+kk…); „Plzeň“ nebo prázdný název = „ověřte druh“.
 *   · KAŽDÁ POLOŽKA JEN JEDNOU — u víc cílů ji započítá jen první v pořadí
 *     plánu (`vlastniciMajetku`); vlastní nemovitost při splácené hypotéce
 *     dostane štítek „ověřte zatížení“.
 *
 * Druh položky: `typ` nefinančního majetku (analyzaV2/nefinancni.js), u řádků
 * z datového modelu bez typu podle id (n1–n6, f1 — propsaniMajetku
 * .VYCHOZI_TYP_NEFIN), u dědictví `druhDedictvi` (analyzaV2/budouciMajetek.js).
 *
 * ULOŽENÁ VOLBA, KTERÁ UŽ NEPLATÍ (auto uložené jako zástava ve starších
 * datech, položka smazaná z analýzy) se NEPOČÍTÁ: solver ji zahodí
 * (`planSolver.volbaDofinancovani` s analýzou), karta řekne proč.
 *
 * Čistý modul bez Reactu — čte ho karta (DofinancovaniCile) i solver.
 */
import { asArr, asObj } from "./bezpecneHodnoty";
import {
  znamyTyp, NEMOVITOST_BYDLENI, NEMOVITOST_PRONAJEM, KOMERCNI, POZEMEK, VOZIDLO, CENNOSTI, FIRMA, BUDOUCI,
} from "./analyzaV2/nefinancni";
import { VYCHOZI_TYP_NEFIN } from "./analyzaV2/propsaniMajetku";
import { DEDICTVI_NEMOVITOST, DEDICTVI_JINE } from "./analyzaV2/budouciMajetek";

export const ZPUSOBY_DOFINANCOVANI = Object.freeze(["prodej", "zastava", "jiny"]);

/** Třída položky pro krok 3. */
export const TRIDA = Object.freeze({
  NEMOVITOST: "nemovitost",                 // byt, dům, pozemek, chata, pronájem, komerční
  MOVITY: "movity",                         // auto, cennosti, zlato, podíl ve firmě
  DEDICTVI_NEMOVITOST: "dedictviNemovitost", // nemovitost po rodičích
  DEDICTVI_PENIZE: "dedictviPenize",       // dědictví v penězích / jiné / neurčené
  NEZNAMY: "neznamy",
});

/** Štítky u čipu (klíč = `kartaCile.dofStitek…`). */
export const STITEK = Object.freeze({
  OCEKAVANE: "ocekavane", OVERTE_DRUH: "overteDruh", STEHOVANI: "stehovani",
  /* Vlastní nemovitost, když klient splácí hypotéku (dočištění 5. 10. 2026):
   * na kterou nemovitost je hypotéka zapsaná, analýza neříká — volná hodnota
   * k zástavě i výnos z prodeje můžou být o jistinu nižší. */
  ZATIZENI: "zatizeni",
});

/** Který způsob smí kterou třídu nabídnout. */
const ZPUSOBY_TRIDY = {
  [TRIDA.NEMOVITOST]: ["zastava", "prodej"],
  [TRIDA.DEDICTVI_NEMOVITOST]: ["zastava", "prodej"],
  [TRIDA.MOVITY]: ["jiny"],
  [TRIDA.DEDICTVI_PENIZE]: ["jiny"],
  [TRIDA.NEZNAMY]: ["jiny"],
};

const TYPY_NEMOVITOST = new Set([NEMOVITOST_BYDLENI, NEMOVITOST_PRONAJEM, KOMERCNI, POZEMEK]);
const TYPY_MOVITE = new Set([VOZIDLO, CENNOSTI, FIRMA]);

/* Název se čte po SLOVECH (ne regexem s \b — ten v JS nezná diakritiku,
 * „Dům Zlatníky“ by byl zlato). Slovo začíná některým z prefixů / je ve výčtu;
 * víceslovné výrazy (vietnamské „ô tô“) se hledají jako celá fráze. */
const slova = (s) => String(s ?? "").normalize("NFC").toLowerCase().split(/[\s.,;:!?()[\]{}\/\\|+&*'\u0022\u201e\u201c\u201a\u2018\u00b7\u2022\u2013\u2014-]+/).filter(Boolean);
const obsahuje = (nazev, prefixy, cela = [], fraze = []) => {
  const w = slova(nazev);
  if (w.some((x) => prefixy.some((p) => x.startsWith(p)) || cela.includes(x))) return true;
  const veta = ` ${w.join(" ")} `;
  return fraze.some((f) => veta.includes(` ${f} `));
};

/* JAK NÁZEV ROZHODUJE (dočištění 5. 10. 2026 — „2 auta“, „Skoda“, „Motocykl“,
 * „Zlaté mince“, „Úspory“ se v řádku s výchozím typem „Nemovitost k bydlení“
 * nabízely k zástavě). Tři skupiny slov:
 *   · SILNĚ MOVITÉ — obecná slova o vozidlech, penězích a investicích. Přebijí
 *     i slovo nemovitosti („Auto v garáži“ garáž není).
 *   · NEMOVITOST — byt, dům, pozemek, chata, 2+kk… Přebije značku auta
 *     („Byt Octaviánova“, „Dům Fabiánova“, „Byt Škodova“ jsou nemovitosti).
 *   · SLABĚ MOVITÉ — značky a modely aut, zlato, umění: rozhodnou jen tam,
 *     kde název o nemovitosti nemluví. */
const MOVITE_SILNE_PREFIXY = [
  "aut", "auť", "vozid", "motork", "motocykl", "motorov", "skútr", "skutr", "karavan", "obytňá", "traktor",
  "přívěs", "čtyřkolk", "dodávk", "dodavk", "elektromob", "člun", "jacht", "letadl",
  "hodink", "šperk", "sbírk", "krypto", "bitcoin", "akci", "fond", "etf", "dluhopis", "obligac",
  "účet", "účt", "ucet", "hotovost", "peníz", "peněz", "penez", "peniaz", "penize", "vklad",
  "spoření", "sporeni", "spořic", "sporic", "penzij", "stavebk",
  /* Vybavení a technika („Vybavení bytu“, „Bytové vybavení“, „Zahradní
   * technika“) — slovo byt/zahrada tu nemovitost neznamená. */
  "vybaven", "nábyt", "nabyt", "technik", "elektronik", "spotřebič", "spotrebic",
];
const MOVITE_SILNE_CELA = [
  "moto", "vůz", "vozy", "loď", "lodě", "lodi", "car", "cars", "cash", "úspory", "úspor", "uspory",
  "mince", "mincí", "minci", "dps", "dip", "xe", "tiền", "quỹ",
];
const MOVITE_SILNE_FRAZE = ["ô tô", "tài khoản", "cổ phiếu", "tiết kiệm", "trái phiếu"];
const MOVITE_SLABE_PREFIXY = [
  "škod", "skod", "octavi", "fabi", "superb", "kodiaq", "karoq", "kamiq", "rapid", "enyaq", "felici", "favorit",
  "roomster", "bmw", "audi", "mercedes", "volkswagen", "toyot", "hyundai", "ford", "opel", "renault", "peugeot",
  "citroen", "citroën", "dacia", "tesla", "volvo", "mazda", "honda", "nissan", "suzuki", "mitsubishi", "subaru",
  "lexus", "porsche", "passat", "tiguan", "touran", "fiat", "jeep", "yamaha", "kawasaki", "ducati", "harley",
  "vespa", "jawa", "babet", "chevrolet", "cupra", "jaguar", "rolex", "stříbr", "platin",
];
const MOVITE_SLABE_CELA = [
  "vw", "kia", "seat", "golf", "polo", "scala", "yeti", "lada", "tatra",
  "zlato", "zlata", "zlatem", "zlaté", "zlatý", "zlatou", "zlatých", "umění", "obraz", "obrazy", "vàng",
];
const NEMOVITOST_PREFIXY = [
  "byt", "dům", "domu", "domek", "domk", "chat", "chalup", "nemovit", "nehnuteľn", "nehnutel", "pozem", "parcel",
  "garáž", "garaz", "komerč", "kancelář", "apartm", "rekrea", "garsoni", "garsonk", "zahrad", "činžov",
  "činžák", "dvojdům", "řadovk", "mezonet", "penzion", "usedlost", "vinic",
];
const NEMOVITOST_CELA = ["dom", "les", "lesy", "pole", "rd", "vila", "vilu", "statek", "house", "flat", "nhà", "đất"];
const NEMOVITOST_FRAZE = ["căn hộ", "chung cư", "biệt thự"];
/* Dispozice bytu („2+kk“, „3+1“, „2KK“) — číslice jsou ASCII, \b tu funguje. */
const DISPOZICE = /\d\s*\+\s*(?:kk|\d)\b|\b\d\s?kk\b/i;

const jeSilneMovity = (n) => obsahuje(n, MOVITE_SILNE_PREFIXY, MOVITE_SILNE_CELA, MOVITE_SILNE_FRAZE);
const jeNemovitostNazev = (n) => DISPOZICE.test(String(n ?? "")) || obsahuje(n, NEMOVITOST_PREFIXY, NEMOVITOST_CELA, NEMOVITOST_FRAZE);
const jeSlabeMovity = (n) => obsahuje(n, MOVITE_SLABE_PREFIXY, MOVITE_SLABE_CELA);
/** Co o druhu říká NÁZEV položky: "movity" | "nemovitost" | null (nic). */
export function druhPodleNazvu(n) {
  if (jeSilneMovity(n)) return "movity";
  if (jeNemovitostNazev(n)) return "nemovitost";
  if (jeSlabeMovity(n)) return "movity";
  return null;
}
/* Rekreační objekt není „vlastní bydlení“ — štítek „jen při stěhování“ nedostane. */
const jeRekreace = (n) => obsahuje(n, ["chat", "chalup", "rekrea"]);
/* DRUŽSTEVNÍ BYT (dočištění 5. 10. 2026, „domysli celou matici“): klient
 * vlastní podíl v družstvu, ne byt — k hypotéce ho zastavit nejde, prodat
 * (převést podíl) ano. */
export const jeDruzstevni = (n) => obsahuje(n, ["družstev", "druzstev"]);

const num = (v) => (Number.isFinite(+v) ? +v : 0);
const txt = (v) => String(v ?? "").trim();

/* OTEVŘENÁ HYPOTÉKA V ZÁVAZCÍCH (tab Hypotéky: druh hypotéka / americká
 * hypotéka, starý řádek bez druhu s textem „hypot…“) — jistina nebo splátka
 * > 0. Stejné pravidlo jako planSolver.maHypotekuVZavazcich. */
const DRUHY_HYPOTEKY = new Set(["hypoteka", "americka_hypoteka"]);
/** `{ ma: boolean, jistina: number }` — součet jistin otevřených hypoték. */
export function hypotekyVZavazcich(d) {
  let ma = false, jistina = 0;
  for (const pole of ["zavazkyB", "zavazkyO"]) {
    for (const r of asArr(asObj(d)[pole])) {
      if (!(num(r.jistina) > 0 || num(r.splatka) > 0)) continue;
      const druh = txt(r.druh);
      if (!(druh ? DRUHY_HYPOTEKY.has(druh) : pole === "zavazkyB" && /hypot/i.test(txt(r.oznaceni)))) continue;
      ma = true;
      jistina += Math.max(0, Math.round(num(r.jistina)));
    }
  }
  return { ma, jistina };
}

/** Třída řádku nefinančního majetku (`kat` = nemovitosti | firma | dedictvi). */
export function tridaPolozky(kat, r) {
  const nazev = txt(r?.oznaceni || r?.nazev);
  if (kat === "dedictvi") {
    if (txt(r?.druhDedictvi) === DEDICTVI_NEMOVITOST) return { trida: TRIDA.DEDICTVI_NEMOVITOST, typ: DEDICTVI_NEMOVITOST };
    return { trida: TRIDA.DEDICTVI_PENIZE, typ: txt(r?.druhDedictvi) === DEDICTVI_JINE ? DEDICTVI_JINE : null };
  }
  if (kat === "firma") return { trida: TRIDA.MOVITY, typ: FIRMA };
  const vyslovny = txt(r?.typ);
  const zModelu = VYCHOZI_TYP_NEFIN[txt(r?.id)] || null;              // řádek z datového modelu (n1–n6)
  let typ = null;
  if (vyslovny) typ = znamyTyp(vyslovny) ? vyslovny : null;          // typ mimo číselník = neznámý
  else typ = zModelu;
  const rika = druhPodleNazvu(nazev);
  if (!typ) {
    /* Starý řádek bez typu: jen jasná nemovitost podle názvu, jinak neznámý. */
    if (!vyslovny && rika === "nemovitost") return { trida: TRIDA.NEMOVITOST, typ: null };
    return { trida: TRIDA.NEZNAMY, typ: null };
  }
  if (typ === BUDOUCI) return { trida: TRIDA.DEDICTVI_PENIZE, typ: null };
  /* Typ movitý, název mluví o nemovitosti (řádek n4 přejmenovaný na „Chata“) → ověřit. */
  if (TYPY_MOVITE.has(typ)) return rika === "nemovitost" ? { trida: TRIDA.NEZNAMY, typ } : { trida: TRIDA.MOVITY, typ };
  if (TYPY_NEMOVITOST.has(typ)) {
    if (rika === "movity") {
      /* Značka auta / zlato v názvu („Octaviánova 12“, „Fabiánova“) u typu,
       * který poradce u řádku SÁM vybral (pronájem, komerční, pozemek — ne
       * výchozí „k bydlení“, ne typ z datového modelu): platí typ. Obecné
       * slovo o vozidle, penězích nebo investici („Auto“, „Úspory“) ne. */
      if (vyslovny && typ !== NEMOVITOST_BYDLENI && !jeSilneMovity(nazev)) return { trida: TRIDA.NEMOVITOST, typ };
      return { trida: TRIDA.NEZNAMY, typ };
    }
    if (rika === "nemovitost") return { trida: TRIDA.NEMOVITOST, typ };
    /* Název nic neříká („Plzeň“, prázdný). „Nemovitost k bydlení“ je VÝCHOZÍ
     * typ nového řádku — kdo ho nepřepnul, nic nezvolil. Platí jen u řádku
     * vlastního bydlení z datového modelu (n1) a u typu, který někdo vybral
     * (pronájem, komerční, pozemek). Jinak „ověřte druh“. */
    const zvoleny = typ !== NEMOVITOST_BYDLENI || zModelu === NEMOVITOST_BYDLENI;
    return zvoleny ? { trida: TRIDA.NEMOVITOST, typ } : { trida: TRIDA.NEZNAMY, typ };
  }
  return { trida: TRIDA.NEZNAMY, typ };
}

/** Štítky položky u daného způsobu. */
export function stitkyPolozky(x, zpusob) {
  if (!x) return [];
  const out = [];
  if (x.trida === TRIDA.NEZNAMY) out.push(STITEK.OVERTE_DRUH);
  if (x.trida === TRIDA.DEDICTVI_PENIZE) {
    out.push(STITEK.OCEKAVANE);
    if (!x.typ) out.push(STITEK.OVERTE_DRUH);                          // dědictví bez zvoleného druhu
  }
  if (x.trida === TRIDA.DEDICTVI_NEMOVITOST && zpusob === "prodej") out.push(STITEK.OCEKAVANE);
  if (x.trida === TRIDA.NEMOVITOST && zpusob === "prodej" && x.typ !== NEMOVITOST_PRONAJEM
    && x.typ !== KOMERCNI && x.typ !== POZEMEK && !jeRekreace(x.nazev)) out.push(STITEK.STEHOVANI);
  if (x.trida === TRIDA.NEMOVITOST && x.hypoteka && (zpusob === "zastava" || zpusob === "prodej")) out.push(STITEK.ZATIZENI);
  return out;
}

/**
 * VEŠKERÝ nefinanční majetek z analýzy s hodnotou > 0, každá položka se svou
 * třídou a způsoby, kterými se dá použít. `druh` = kategorie v analýze
 * (nemovitost | dedictvi | firma) — beze změny proti dřívějšku.
 * Vlastní nemovitost nese `hypoteka: { jistina }`, když klient splácí
 * hypotéku (štítek „ověřte zatížení“), jinak `hypoteka: null`.
 * @returns {Array<{id, nazev, hodnota, druh, trida, typ, zpusoby: string[], hypoteka: object|null}>}
 */
export function majetekKDofinancovani(d) {
  const m = asObj(d?.majetek);
  const hypo = hypotekyVZavazcich(d);
  const out = [];
  const vem = (pole, kat, druh) => asArr(pole).forEach((x, i) => {
    const hodnota = Math.round(num(x?.hodnota));
    if (!(hodnota > 0)) return;
    const { trida, typ } = tridaPolozky(kat, x);
    const nazev = txt(x?.oznaceni || x?.nazev);
    const druzstevni = trida === TRIDA.NEMOVITOST && jeDruzstevni(nazev);
    out.push({
      id: String(x?.id ?? `${druh}${i}`), nazev, hodnota, druh,
      trida, typ, zpusoby: druzstevni ? ["prodej"] : ZPUSOBY_TRIDY[trida] || [],
      hypoteka: trida === TRIDA.NEMOVITOST && hypo.ma ? { jistina: hypo.jistina } : null,
      ...(druzstevni ? { druzstevni: true } : {}),
    });
  });
  vem(m.nemovitosti, "nemovitosti", "nemovitost");
  vem(m.dedictvi, "dedictvi", "dedictvi");
  vem(m.firma, "firma", "firma");
  return out;
}

/* Pořadí v nabídce: nejdřív to, co jde použít bez výhrad. U zástavy byt
 * rodičů (dědictví) před vlastní nemovitostí — tak to majitel diktoval. */
const PORADI = {
  zastava: [TRIDA.DEDICTVI_NEMOVITOST, TRIDA.NEMOVITOST],
  prodej: [TRIDA.NEMOVITOST, TRIDA.DEDICTVI_NEMOVITOST],
  jiny: [TRIDA.MOVITY, TRIDA.DEDICTVI_PENIZE, TRIDA.NEZNAMY],
};

/**
 * Nabídka čipů pro způsob — jen vhodné položky, se štítky.
 * Bez výhrad (bez štítku) jdou první; uvnitř skupiny pořadí podle třídy.
 * @param {Array} majetek výstup `majetekKDofinancovani`
 * @param {"prodej"|"zastava"|"jiny"} zpusob
 */
export function nabidkaDofinancovani(majetek, zpusob) {
  const poradi = PORADI[zpusob];
  if (!poradi) return [];
  const vhodne = asArr(majetek)
    .filter((x) => Array.isArray(x.zpusoby) && x.zpusoby.includes(zpusob))   // asArr bere jen objekty
    .map((x) => ({ ...x, stitky: stitkyPolozky(x, zpusob) }));
  const klic = (x) => (x.stitky.length ? 100 : 0) + poradi.indexOf(x.trida);
  return vhodne.map((x, i) => [x, i]).sort((a, b) => klic(a[0]) - klic(b[0]) || a[1] - b[1]).map(([x]) => x);
}

/**
 * Platí uložená volba dofinancování nad TĚMITO daty analýzy?
 *   · bez položky (`majetekId` prázdné) = hodnota zadaná ručně → platí,
 *   · položka, která se k tomu způsobu nehodí (auto jako zástava) → `druh`,
 *   · položka, která v analýze už není (smazaná, hodnota 0) → `chybi`,
 *   · zástava u úvěru, kde zástava nejde (spotřebitelský úvěr) → `uver`,
 *   · položka, kterou už započítal jiný cíl (`vlastnici` z `vlastniciMajetku`)
 *     → `pouzito` (+ `cilId` cíle, kterému položka patří).
 * Neplatná volba se NEPOČÍTÁ — karta ukáže proč a nabídne ji odebrat.
 * @param {object|null} volba `{ zpusob, castka, nazev, majetekId }`
 * @param {object} d analýza
 * @param {{ spotr?: boolean, vlastnici?: Map<string,string>|null, cilId?: string }} [o]
 *   `spotr` = cíl na spotřebitelský úvěr; `vlastnici` + `cilId` = hlídání,
 *   aby se jedna položka nezapočítala u dvou cílů
 * @returns {{ platna: boolean, duvod: null|"druh"|"chybi"|"uver"|"pouzito", polozka: object|null, cilId?: string }}
 */
export function platnostVolbyDofinancovani(volba, d, { spotr = false, vlastnici = null, cilId = null } = {}) {
  const v = asObj(volba);
  if (!ZPUSOBY_DOFINANCOVANI.includes(v.zpusob)) return { platna: true, duvod: null, polozka: null };
  if (spotr && v.zpusob === "zastava") return { platna: false, duvod: "uver", polozka: null };
  if (v.majetekId == null || txt(v.majetekId) === "") return { platna: true, duvod: null, polozka: null };
  const polozka = majetekKDofinancovani(d).find((x) => x.id === String(v.majetekId)) || null;
  if (!polozka) {
    /* Bez načteného majetku nejde soudit — volba zůstává (nic se nemaže naslepo). */
    const maMajetek = Object.keys(asObj(asObj(d).majetek)).length > 0;
    return maMajetek ? { platna: false, duvod: "chybi", polozka: null } : { platna: true, duvod: null, polozka: null };
  }
  if (!polozka.zpusoby.includes(v.zpusob)) return { platna: false, duvod: "druh", polozka };
  if (vlastnici instanceof Map && cilId != null) {
    const vlastnik = vlastnici.get(polozka.id);
    if (vlastnik != null && vlastnik !== String(cilId)) return { platna: false, duvod: "pouzito", polozka, cilId: vlastnik };
  }
  return { platna: true, duvod: null, polozka };
}

/**
 * KAŽDÁ POLOŽKA MAJETKU JEN JEDNOU (dočištění 5. 10. 2026): auto vybrané jako
 * „jiný majetek“ u Bydlení i u cíle Auto by se prodalo dvakrát, byt zastavený
 * u jednoho cíle a prodaný u druhého by se počítal obojí. Položka patří PRVNÍMU
 * cíli v pořadí plánu (pořadí solveru = pořadí karet), u kterého je její volba
 * jinak platná; u dalších cílů volba neplatí (`duvod: "pouzito"`) a karta
 * řekne, u kterého cíle už je. Ručně zadaná hodnota (bez položky) se nehlídá.
 * @param {object} mapa `navrh.solver.dofinancovani` — `{ [idCile]: volba }`
 * @param {Array<{id: string, spotr?: boolean}>} cile cíle na úvěr v pořadí plánu
 * @param {object} d analýza
 * @returns {Map<string, string>} id položky → id cíle, kterému patří
 */
export function vlastniciMajetku(mapa, cile, d) {
  const out = new Map();
  const m = asObj(mapa);
  for (const c of asArr(cile)) {
    if (c.id == null) continue;
    const v = asObj(m[c.id]);
    if (!ZPUSOBY_DOFINANCOVANI.includes(v.zpusob)) continue;
    if (!(Number.isFinite(+v.castka) && +v.castka > 0)) continue;     // bez částky se nepočítá
    if (v.majetekId == null || txt(v.majetekId) === "") continue;
    const id = String(v.majetekId);
    if (out.has(id)) continue;
    if (!platnostVolbyDofinancovani(v, d, { spotr: !!c.spotr }).platna) continue;
    out.set(id, String(c.id));
  }
  return out;
}
